import * as React from 'react';

import classnames from 'classnames';

import styles from './button-card.module.css';

type Props = {
    // Not currently possible with flow
    // ...HTMLButtonElement,
    onClick?: (e: React.SyntheticEvent<any>) => void;
    children: React.ReactNode;
    autoFocus?: boolean;
    isSelected?: boolean;
    /** If true, the card will size vertically according to the content, not the parent's height */
    fitHeight?: boolean;
    /** If true, the card's border will not look "clickable" */
    isDisabled?: boolean;
    isFullWidth?: boolean;
    needsAttention?: boolean;
    isTransparent?: boolean;
    isFlex?: boolean;
    /** If true, the card's opacity will be reduced if it is disabled */
    showDisabledOverlay?: boolean;
    /** If true, the card's opacity will be reduced if it isSelected is false */
    showUnselectedOverlay?: boolean;
    hasOffWhiteBackground?: boolean;
    hasOffWhiteHover?: boolean;
    borderVariant?: 'default' | 'ai' | 'feature';
    className?: string;
    tooltipText?: string;
};

ButtonCard.displayName = 'ButtonCard';

/**
 * This component uses our simple card styling, but acts
 * as a form element, and thus contains hover/active/focus states.
 *
 * This is usually used to select a single rich option, usually as the first selection in a series of forms.
 */
export function ButtonCard({
    children,
    onClick,
    isSelected,
    fitHeight,
    isFullWidth,
    needsAttention,
    isDisabled,
    isTransparent,
    isFlex,
    showDisabledOverlay,
    showUnselectedOverlay,
    hasOffWhiteBackground,
    hasOffWhiteHover,
    borderVariant = 'default',
    className,
    tooltipText,
    ...otherProps
}: Props) {
    const styleNames = classnames(
        {
            [styles['button-card']]: true,
            [styles['button-card--off-white-hover']]: hasOffWhiteHover,
            [styles['button-card--dark-bg']]: hasOffWhiteBackground,
            [styles['button-card--selected']]: isSelected,
            [styles['button-card--fitHeight']]: fitHeight,
            [styles['button-card--fullWidth']]: isFullWidth,
            [styles['button-card--transparent']]: isTransparent,
            [styles['button-card--flex']]: isFlex,
            [styles['button-card--needsAttention']]: needsAttention,
            [styles['button-card--noClick']]: !onClick,
            [styles['button-card--disabled']]: isDisabled,
            [styles['button-card--disabled-overlay']]:
                (isDisabled && showDisabledOverlay) || (!isSelected && showUnselectedOverlay),
            [styles['button-card--border-ai']]: borderVariant === 'ai',
            [styles['button-card--border-feature']]: borderVariant === 'feature',
        },
        className
    );

    return (
        <button
            {...otherProps}
            onClick={onClick}
            disabled={isDisabled}
            type='button'
            className={styleNames}
            data-tip={tooltipText}
        >
            {children}
        </button>
    );
}
